import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';

import { roleTypes } from '../../../content/zci/role-types';
import { AccessPermissionsService } from '../../services/access-permissions/access-permissions.service';
import * as accessPermissionsActions from '../actions/access-permissions.actions';

@Injectable({
    providedIn: 'root',
})
export class AccessPermissionsApiEffect {
    private actions$ = inject(Actions);
    private accessPermissionsService = inject(AccessPermissionsService);


    grantPermissionMvp$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(accessPermissionsActions.grantPermissionMvp),
            switchMap((action) => {
                return this.accessPermissionsService
                    .grantPermissionMvp(
                        action.granteeDetails,
                        action.grantorDetails,
                        action.accountName,
                    )
                    .pipe(
                        map((result) => {
                            return accessPermissionsActions.grantPermissionSuccess({
                                data: result,
                                roleType: roleTypes.roles[56].code, // 'GIPR'
                                accessLevel: action.granteeDetails.accessLevel,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(accessPermissionsActions.grantPermissionError({ error }));
                        }),
                    );
            }),
        );
    });

    removePermission$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(accessPermissionsActions.removePermission),
            switchMap((action) => {
                return this.accessPermissionsService
                    .removePermission(action.granteeDetails, action.grantorDetails)
                    .pipe(
                        map((result) => {
                            return accessPermissionsActions.removePermissionSuccess({
                                data: result,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(accessPermissionsActions.removePermissionError({ error }));
                        }),
                    );
            }),
        );
    });

    bulkGrantPermission$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(accessPermissionsActions.bulkGrantPermission),
            switchMap((action) => {
                return this.accessPermissionsService
                    .bulkGrantPermission(
                        action.granteeDetails,
                        action.grantorDetails,
                        action.accounts,
                    )
                    .pipe(
                        map((result) => {
                            return accessPermissionsActions.bulkGrantPermissionSuccess({
                                data: result,
                                roleType: roleTypes.roles[56].code, // 'GIPR'
                                accessLevel: action.granteeDetails.accessLevel,
                            });
                        }),
                        catchError((error: HttpErrorResponse) => {
                            return of(
                                accessPermissionsActions.bulkGrantPermissionError({ error }),
                            );
                        }),
                    );
            }),
        );
    });

    bulkRemovePermission$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(accessPermissionsActions.bulkRemovePermission),
            switchMap((action) => {
                return forkJoin(
                    action.requests.map((request) =>
                        this.accessPermissionsService.bulkRemovePermission(
                            request.granteeDetails,
                            request.grantorDetails,
                            request.accounts,
                        ),
                    ),
                ).pipe(
                    map((results) =>
                        accessPermissionsActions.bulkRemovePermissionSuccess({
                            data: { results: results.flatMap((r) => r.results) },
                        }),
                    ),
                    catchError((error: HttpErrorResponse) =>
                        of(accessPermissionsActions.bulkRemovePermissionError({ error })),
                    ),
                );
            }),
        );
    });
}
